import { useState, type FormEvent, useEffect } from 'react';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Alert from '@/components/ui/Alert';
import { api, ApiClientError } from '@/services/api';
import { useAuthStore } from '@/store/authStore';

/**
 * Topic type for routing contact messages to appropriate team.
 * WP-CONTACT-1: Unified contact modal with topic support.
 */
export type ContactTopic = 'support' | 'billing' | 'privacy' | 'legal' | 'security' | 'dpo';

export interface ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Optional: Pre-select topic for routing (support, billing, privacy, etc.) */
  topic?: ContactTopic;
  /** Optional: Additional context (e.g., current page URL, error details) */
  context?: string;
}

interface ContactFormData {
  name: string;
  email: string;
  message: string;
  website: string; // Honeypot field - must be empty
  formLoadTime: number; // Timestamp when form was opened
  topic?: string; // WP-CONTACT-1: Topic for routing
  context?: string; // WP-CONTACT-1: Page/error context
  // NOTE: tenant_id is NOT sent from frontend - it's extracted from the
  // authenticated session on the backend for security
}

/**
 * Contact Modal
 *
 * Public contact form for reaching FlowIO support.
 * WP-CONTACT-1: Enhanced with topic, context, and auth pre-fill.
 * Includes client-side bot detection (honeypot + timing).
 * Backend handles actual spam detection.
 */
export default function ContactModal({ isOpen, onClose, topic, context }: ContactModalProps) {
  // WP-CONTACT-1: Get auth state for pre-filling form fields
  // NOTE: tenant_id is handled on the backend from the auth session, not sent from frontend
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const userEmail = useAuthStore((state) => state.userEmail);
  const userName = useAuthStore((state) => state.userName);

  // Form fields - pre-fill for authenticated users
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');

  // Pre-fill name and email when authenticated user opens modal
  useEffect(() => {
    if (isOpen && isAuthenticated) {
      if (userName && !name) setName(userName);
      if (userEmail && !email) setEmail(userEmail);
    }
  }, [isOpen, isAuthenticated, userName, userEmail, name, email]);
  const [website, setWebsite] = useState(''); // Honeypot
  const [openedAt] = useState(() => Date.now());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const isValid = name.trim() && email.trim() && message.trim();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!isValid) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const formData: ContactFormData = {
        name: name.trim(),
        email: email.trim(),
        message: message.trim(),
        website, // Honeypot - backend will detect if filled
        formLoadTime: openedAt / 1000, // Convert ms to seconds (Python time.time() uses seconds)
        // WP-CONTACT-1: Topic and context for routing
        // NOTE: tenant_id extracted from auth session on backend, not sent from frontend
        topic: topic || undefined,
        context: context || undefined,
      };

      await api.post('/contact', formData);
      setSuccess(true);
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.detail);
      } else {
        setError('Failed to send message. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setName('');
    setEmail('');
    setMessage('');
    setWebsite('');
    setError(null);
    setSuccess(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="contact-modal-title"
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
        {success ? (
          // Success state
          <div className="text-center py-6">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
              <svg
                className="w-8 h-8 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">
              Message Sent
            </h2>
            <p className="text-gray-600 mb-6">
              Thank you for contacting us. We will get back to you soon.
            </p>
            <Button onClick={handleClose}>Close</Button>
          </div>
        ) : (
          // Form state
          <>
            <h2 id="contact-modal-title" className="text-lg font-semibold text-gray-900 mb-4">
              Contact Us
            </h2>

            <p className="text-sm text-gray-600 mb-4">
              Have a question or feedback? Send us a message and we will get back to you.
            </p>

            {error && (
              <Alert variant="error" className="mb-4" onDismiss={() => setError(null)}>
                {error}
              </Alert>
            )}

            {/* WP-CONTACT-1: Show authenticated user indicator */}
            {isAuthenticated && userEmail && (
              <p className="text-sm text-gray-500 mb-4">
                Sending as <span className="font-medium">{userEmail}</span>
              </p>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <Input
                label="Name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                autoComplete="name"
                placeholder="Your name"
              />

              <Input
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                placeholder="your@email.com"
              />

              <div>
                <label
                  htmlFor="contact-message"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Message
                </label>
                <textarea
                  id="contact-message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  required
                  rows={4}
                  className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-500 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 text-sm"
                  placeholder="How can we help you?"
                />
              </div>

              {/* Honeypot field - hidden from users, bots will fill it */}
              <div className="hidden" aria-hidden="true">
                <label htmlFor="contact-website">Website</label>
                <input
                  id="contact-website"
                  type="text"
                  name="website"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  tabIndex={-1}
                  autoComplete="off"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={!isValid}
                  isLoading={isSubmitting}
                  className="flex-1"
                >
                  Send Message
                </Button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
